import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  PhoneIcon,
  LockClosedIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

type InvoiceRow = {
  id: number;
  number: string;
  createdDate: string;
  dueDate: string;
  amountToPay: number;
  currencyCode: string;
  status: number;
  maxExtendDays: number;
  maxDueDate: string;
  alreadyExtended?: boolean;
};

type Step = 'phone' | 'code' | 'invoices';
type ExtendMode = 'days' | 'calendar';

const MAX_DAYS = 20;
const DEFAULT_SESSION_TTL = 300;

const formatMoney = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount || 0);

const formatDate = (value: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatRemaining = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

const toInputDate = (value: string) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value.slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysToIsoDate = (isoDate: string, days: number) => {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const beaconLogout = () => {
  try {
    const fd = new FormData();
    fd.append('action', 'logout');
    navigator.sendBeacon('/api/payment-extension.php', fd);
  } catch {
    /* ignore */
  }
};

const PaymentExtension: React.FC = () => {
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [clientName, setClientName] = useState('');
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [extendMode, setExtendMode] = useState<ExtendMode>('days');
  const [days, setDays] = useState(10);
  const [calendarDate, setCalendarDate] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const verifiedRef = useRef(false);

  const selected = useMemo(
    () => invoices.find((inv) => inv.id === selectedId) || null,
    [invoices, selectedId]
  );

  const minCalendarDate = selected ? addDaysToIsoDate(toInputDate(selected.dueDate), 1) : '';
  const maxCalendarDate = selected?.maxDueDate || '';

  const previewDue = useMemo(() => {
    if (!selected) return '';
    if (extendMode === 'days') {
      return addDaysToIsoDate(toInputDate(selected.dueDate), days);
    }
    return calendarDate || '';
  }, [selected, extendMode, days, calendarDate]);

  const clearLocalSession = useCallback((expiredMessage?: string) => {
    verifiedRef.current = false;
    setExpiresAt(null);
    setRemainingSec(0);
    setStep('phone');
    setPhone('');
    setCode('');
    setInvoices([]);
    setSelectedId(null);
    setClientName('');
    setPhoneHint('');
    setMessage('');
    setSuccessMsg('');
    setDays(10);
    setCalendarDate('');
    setExtendMode('days');
    setError(expiredMessage || '');
  }, []);

  const armSession = useCallback((expiresIn?: number, sessionTtl?: number) => {
    const ttl = Math.max(
      1,
      Number(expiresIn) > 0 ? Number(expiresIn) : Number(sessionTtl) || DEFAULT_SESSION_TTL
    );
    verifiedRef.current = true;
    setExpiresAt(Date.now() + ttl * 1000);
    setRemainingSec(ttl);
  }, []);

  const startOver = useCallback(
    async (expiredMessage?: string) => {
      try {
        await fetch('/api/payment-extension.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ action: 'logout' }),
        });
      } catch {
        /* ignore */
      }
      clearLocalSession(expiredMessage);
    },
    [clearLocalSession]
  );

  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/payment-extension.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'invoices' }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.locked) {
          await startOver('Session expired. Enter your phone again.');
          return;
        }
        throw new Error(data.error || 'Could not load invoices');
      }
      setClientName(data.clientName || '');
      setPhoneHint(data.phoneHint || '');
      const list: InvoiceRow[] = Array.isArray(data.invoices) ? data.invoices : [];
      setInvoices(list);
      armSession(data.expiresIn, data.sessionTtl);
      setStep('invoices');
      window.AirmaxVisitor?.identify({
        clientName: data.clientName || '',
        clientPhoneHint: data.phoneHint || '',
        clientContext: 'payment-extension',
        supportAction: 'Viewing extension invoices',
      });
      if (list.length === 1) {
        setSelectedId(list[0].id);
        setCalendarDate(
          addDaysToIsoDate(toInputDate(list[0].dueDate), Math.min(10, list[0].maxExtendDays || MAX_DAYS))
        );
      } else if (selectedId && !list.some((i) => i.id === selectedId)) {
        setSelectedId(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Always require a fresh login on page open
  useEffect(() => {
    void (async () => {
      try {
        await fetch('/api/payment-extension.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ action: 'logout' }),
        });
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Sign out when the tab is closed or refreshed
  useEffect(() => {
    const onLeave = () => {
      if (verifiedRef.current) {
        beaconLogout();
      }
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('beforeunload', onLeave);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('beforeunload', onLeave);
    };
  }, []);

  // Live countdown — auto sign-out at 0
  useEffect(() => {
    if (!expiresAt || step !== 'invoices') return undefined;
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setRemainingSec(left);
      if (left <= 0) {
        void startOver('Signed out for security. Verify your phone again.');
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, step, startOver]);

  useEffect(() => {
    if (!selected || selected.alreadyExtended) return;
    const next = addDaysToIsoDate(
      toInputDate(selected.dueDate),
      Math.min(days, selected.maxExtendDays || MAX_DAYS)
    );
    if (extendMode === 'calendar' && !calendarDate) {
      setCalendarDate(next);
    }
  }, [selected, days, extendMode, calendarDate]);

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const res = await fetch('/api/payment-extension.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'send_code', phone }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not send code');
      }
      setPhoneHint(data.phoneHint || '');
      setMessage(data.message || 'Code sent');
      setStep('code');
      setCode('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/payment-extension.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action: 'verify_code', code }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Could not verify code');
      }
      setClientName(data.clientName || '');
      setPhoneHint(data.phoneHint || '');
      armSession(data.expiresIn, data.sessionTtl);
      window.AirmaxVisitor?.identify({
        clientName: data.clientName || '',
        clientPhoneHint: data.phoneHint || '',
        clientContext: 'payment-extension',
        supportAction: 'Verified phone',
      });
      await loadInvoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not verify code');
      setLoading(false);
    }
  };

  const applyExtension = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) {
      setError('Select an invoice first.');
      return;
    }
    setError('');
    setSuccessMsg('');
    setLoading(true);
    try {
      const body: Record<string, string | number> = {
        action: 'extend',
        invoiceId: selected.id,
      };
      if (extendMode === 'days') {
        body.days = days;
      } else {
        if (!calendarDate) {
          throw new Error('Pick a new due date on the calendar.');
        }
        body.newDueDate = calendarDate;
      }
      const res = await fetch('/api/payment-extension.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (data.locked) {
          setStep('phone');
          setError('Session expired. Enter your phone again.');
          return;
        }
        if (data.alreadyExtended) {
          setInvoices((prev) =>
            prev.map((inv) =>
              inv.id === selected.id ? { ...inv, alreadyExtended: true } : inv
            )
          );
        }
        throw new Error(data.error || 'Could not extend due date');
      }
      setSuccessMsg(data.message || 'Due date updated.');
      window.AirmaxVisitor?.note('Applied payment extension');
      if (data.invoice) {
        setInvoices((prev) =>
          prev.map((inv) => (inv.id === data.invoice.id ? { ...inv, ...data.invoice } : inv))
        );
        setSelectedId(data.invoice.id);
        setCalendarDate(
          addDaysToIsoDate(
            toInputDate(data.invoice.dueDate),
            Math.min(10, data.invoice.maxExtendDays || MAX_DAYS)
          )
        );
      } else {
        await loadInvoices();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not extend due date');
    } finally {
      setLoading(false);
    }
  };

  const selectInvoice = (inv: InvoiceRow) => {
    setSelectedId(inv.id);
    setSuccessMsg('');
    setError('');
    if (inv.alreadyExtended) return;
    setCalendarDate(
      addDaysToIsoDate(toInputDate(inv.dueDate), Math.min(days, inv.maxExtendDays || MAX_DAYS))
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <section className="relative bg-gradient-to-br from-airmax-blue to-blue-700 overflow-hidden">
        <div className="py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <CalendarDaysIcon className="h-12 w-12 text-white/90 mx-auto mb-4" />
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">Payment Extension</h1>
              <p className="text-lg text-blue-100 max-w-xl mx-auto">
                Need a little more time? Enter the phone number on your Airmax billing account to
                verify, then extend an unpaid invoice due date.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
            {step === 'phone' && (
              <form onSubmit={sendCode} className="space-y-5">
                <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <LockClosedIcon className="h-5 w-5 text-airmax-blue flex-shrink-0 mt-0.5" />
                  <p>
                    Enter the phone number on your Airmax billing account. We’ll text you a
                    one-time code to confirm it’s you. If the number doesn’t match your account, the
                    system won’t let you continue. For security, you’ll be signed out after a few
                    minutes or if you leave this page.
                  </p>
                </div>
                <div>
                  <label htmlFor="ext-phone" className="block text-sm font-semibold text-gray-800 mb-2">
                    Account phone number
                  </label>
                  <div className="relative">
                    <PhoneIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      id="ext-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(928) 555-1234"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-airmax-blue focus:border-airmax-blue"
                      required
                    />
                  </div>
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-airmax-blue text-white font-semibold py-3 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Sending…' : 'Text me a code'}
                </button>
              </form>
            )}

            {step === 'code' && (
              <form onSubmit={verifyCode} className="space-y-5">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Enter your code</h2>
                  <p className="text-sm text-gray-600">
                    {message || `We sent a 6-digit code to ${phoneHint || 'your phone'}.`}
                  </p>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center text-2xl tracking-widest font-semibold focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                  required
                />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full rounded-xl bg-airmax-blue text-white font-semibold py-3 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Checking…' : 'Verify & show invoices'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('phone');
                    setError('');
                    setMessage('');
                  }}
                  className="w-full text-sm font-medium text-airmax-blue"
                >
                  Use a different number
                </button>
              </form>
            )}

            {step === 'invoices' && (
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {clientName ? `Hi, ${clientName.split(' ')[0]}` : 'Your invoices'}
                    </h2>
                    <p className="text-sm text-gray-600">
                      Verified {phoneHint}. Choose an unpaid invoice and extend the due date.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
                  <ClockIcon className="h-4 w-4 flex-shrink-0" />
                  <span>
                    Auto sign-out in <strong>{formatRemaining(remainingSec)}</strong>
                  </span>
                </div>

                {loading && !selected && <p className="text-sm text-gray-500">Loading invoices…</p>}
                {error && <p className="text-sm text-red-600">{error}</p>}
                {successMsg && (
                  <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    {successMsg}
                  </div>
                )}

                {!loading && invoices.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
                    <p className="font-medium text-gray-800">No unpaid invoices</p>
                    <p className="text-sm text-gray-500 mt-1">Nothing to extend right now.</p>
                  </div>
                )}

                {invoices.length > 0 && (
                  <ul className="space-y-3">
                    {invoices.map((inv) => {
                      const active = inv.id === selectedId;
                      const used = Boolean(inv.alreadyExtended);
                      return (
                        <li key={inv.id}>
                          <button
                            type="button"
                            onClick={() => selectInvoice(inv)}
                            className={`w-full text-left rounded-xl border p-4 transition-colors ${
                              active
                                ? 'border-airmax-blue bg-blue-50 ring-2 ring-airmax-blue/20'
                                : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                            } ${used ? 'opacity-80' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  Invoice #{inv.number || inv.id}
                                </p>
                                <p className="text-sm text-gray-600 mt-0.5">
                                  Due {formatDate(inv.dueDate)} ·{' '}
                                  {formatMoney(inv.amountToPay, inv.currencyCode)}
                                </p>
                              </div>
                              {used && (
                                <span className="shrink-0 rounded-full bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
                                  Already extended
                                </span>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {selected && selected.alreadyExtended && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    This invoice has already used its one-time extension. Please pay by the due date,
                    or call{' '}
                    <a href="tel:928-565-6707" className="font-semibold underline">
                      928-565-6707
                    </a>{' '}
                    if you need help.
                  </div>
                )}

                {selected && !selected.alreadyExtended && (
                  <form onSubmit={applyExtension} className="space-y-4 pt-1 border-t border-gray-100">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <ClockIcon className="h-4 w-4 text-airmax-blue" />
                      <span>
                        Current due: <strong>{formatDate(selected.dueDate)}</strong>
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setExtendMode('days')}
                        className={`rounded-xl py-2.5 text-sm font-semibold border ${
                          extendMode === 'days'
                            ? 'bg-airmax-blue text-white border-airmax-blue'
                            : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Extra days
                      </button>
                      <button
                        type="button"
                        onClick={() => setExtendMode('calendar')}
                        className={`rounded-xl py-2.5 text-sm font-semibold border ${
                          extendMode === 'calendar'
                            ? 'bg-airmax-blue text-white border-airmax-blue'
                            : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Calendar date
                      </button>
                    </div>

                    {extendMode === 'days' ? (
                      <div>
                        <label
                          htmlFor="ext-days"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          How many extra days?
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            id="ext-days"
                            type="range"
                            min={1}
                            max={selected.maxExtendDays || MAX_DAYS}
                            value={days}
                            onChange={(e) => setDays(Number(e.target.value))}
                            className="flex-1 accent-airmax-blue"
                          />
                          <input
                            type="number"
                            min={1}
                            max={selected.maxExtendDays || MAX_DAYS}
                            value={days}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              if (Number.isNaN(n)) return;
                              setDays(
                                Math.min(
                                  selected.maxExtendDays || MAX_DAYS,
                                  Math.max(1, Math.floor(n))
                                )
                              );
                            }}
                            className="w-16 rounded-lg border border-gray-300 px-2 py-2 text-center font-semibold"
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label
                          htmlFor="ext-date"
                          className="block text-sm font-semibold text-gray-800 mb-2"
                        >
                          New due date
                        </label>
                        <input
                          id="ext-date"
                          type="date"
                          min={minCalendarDate}
                          max={maxCalendarDate}
                          value={calendarDate}
                          onChange={(e) => setCalendarDate(e.target.value)}
                          className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-airmax-blue"
                          required
                        />
                      </div>
                    )}

                    {previewDue && (
                      <p className="text-sm text-gray-700 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                        New due date will be{' '}
                        <strong>
                          {formatDate(
                            extendMode === 'days'
                              ? `${previewDue}T12:00:00`
                              : `${calendarDate}T12:00:00`
                          )}
                        </strong>
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-xl bg-airmax-blue text-white font-semibold py-3 hover:bg-blue-700 disabled:opacity-50"
                    >
                      {loading ? 'Updating…' : 'Extend due date'}
                    </button>
                  </form>
                )}

                <div className="pt-2 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => void loadInvoices()}
                    disabled={loading}
                    className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Refresh invoices
                  </button>
                  <button
                    type="button"
                    onClick={() => void startOver()}
                    className="flex-1 rounded-xl border border-gray-300 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-gray-500">
            Need help?{' '}
            <a href="tel:928-565-6707" className="text-airmax-blue font-semibold">
              928-565-6707
            </a>
            {' · '}
            <Link to="/pay-bill" className="text-airmax-blue font-semibold">
              Pay Bill
            </Link>
            {' · '}
            <Link to="/support" className="text-airmax-blue font-semibold">
              Support
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PaymentExtension;
